'use client';

import { useCallback, useEffect, useState } from 'react';
import { useModal } from 'components/shared/Modal';
import { StoreLocatorComponentDiv } from 'components/shared/RetailStoresPopup';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { analytics } from 'lib/analytics/analytics';

type StoreCountriesResponse = {
  countries: Array<{
    iso: string;
    country: string;
    storeCount: number;
  }>;
  totalCountries: number;
};

type StoreLocatorSource = 'hero' | 'footer';

export function useRetailStoreLocator(source: StoreLocatorSource = 'hero') {
  const { region: country } = useRegionLocale();
  const modalClient = useModal();
  const [hasStores, setHasStores] = useState(false);

  useEffect(() => {
    if (!country) return;
    let active = true;
    fetch('/api/stores/countries')
      .then((res) => res.json())
      .then((data: StoreCountriesResponse) => {
        if (active) {
          const countryCodes = data.countries.map((entry) => entry.iso);
          setHasStores(countryCodes.includes(country));
        }
      })
      .catch(() => {
        if (active) setHasStores(false);
      });
    return () => {
      active = false;
    };
  }, [country]);

  const openStoreLocator = useCallback(() => {
    const eventName =
      source === 'footer'
        ? 'Find a store footer - CLICK'
        : 'Get it today - CLICK';
    analytics.track(eventName, { clicked: true });
    modalClient.set(<StoreLocatorComponentDiv />);
    modalClient.setPortal(true);
    modalClient.setFullView();
    modalClient.setCloseButtonTheme('light');
    modalClient.show();
  }, [modalClient, source]);

  return { hasStores, openStoreLocator };
}
